import React, { useState } from 'react';
import {
  Card,
  Button,
  Upload,
  Typography,
  Space,
  Alert,
  Spin,
  Progress,
  List,
  Tag,
  Tooltip,
  Descriptions,
  Collapse,
  Empty,
  message,
} from 'antd';
import {
  UploadOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  QuestionCircleOutlined,
  FileExcelOutlined,
  MailOutlined,
  ThunderboltOutlined,
  SearchOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { API } from '@/services/api.service';

const { Title, Text, Paragraph } = Typography;
const { Dragger } = Upload;
const { Panel } = Collapse;

/**
 * ConfigSuggestionPanel
 *
 * Analyzes uploaded EML/Excel files and suggests matching parser configurations
 * based on structural fingerprinting. Company-agnostic.
 */
export default function ConfigSuggestionPanel({ onConfigSelected, onCreateNew }) {
  const [file, setFile] = useState(null);
  const [suggestionResult, setSuggestionResult] = useState(null);

  // Suggest config mutation
  const suggestMutation = useMutation({
    mutationFn: (formData) => API.smartParser.suggestConfig(formData),
    onSuccess: (response) => {
      setSuggestionResult(response.data);
      if (response.data.suggestions?.length > 0) {
        message.success(`Found ${response.data.suggestions.length} matching configuration(s)`);
      } else {
        message.info('No matching configurations found. Consider creating a new one.');
      }
    },
    onError: (error) => {
      message.error(error.response?.data?.message || 'Failed to analyze file');
    },
  });

  const handleFileUpload = (info) => {
    const { file: uploadedFile } = info;
    if (uploadedFile.status === 'removed') {
      setFile(null);
      setSuggestionResult(null);
      return;
    }
    setFile(uploadedFile.originFileObj || uploadedFile);
  };

  const handleAnalyze = () => {
    if (!file) {
      message.warning('Please upload a file first');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    suggestMutation.mutate(formData);
  };

  const handleSelectConfig = (suggestion) => {
    if (onConfigSelected) {
      onConfigSelected(suggestion.configId, suggestion);
    }
  };

  const handleCreateNew = () => {
    if (onCreateNew) {
      onCreateNew(suggestionResult?.fingerprint);
    }
  };

  const getRecommendationTag = (recommendation) => {
    switch (recommendation) {
      case 'high_confidence':
        return <Tag color="success" icon={<CheckCircleOutlined />}>High Confidence</Tag>;
      case 'medium':
        return <Tag color="warning" icon={<WarningOutlined />}>Medium Match</Tag>;
      case 'low':
        return <Tag color="default" icon={<QuestionCircleOutlined />}>Low Match</Tag>;
      default:
        return <Tag>Unknown</Tag>;
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return '#52c41a';
    if (score >= 75) return '#faad14';
    if (score >= 60) return '#1890ff';
    return '#ff4d4f';
  };

  const renderFingerprint = (fingerprint) => {
    if (!fingerprint) return null;

    return (
      <Collapse ghost>
        <Panel header="Fingerprint Details" key="fingerprint">
          <Descriptions size="small" column={2} bordered>
            <Descriptions.Item label="Sheet Count">
              {fingerprint.structure?.sheetCount || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Sheet Pattern">
              <Tag>{fingerprint.structure?.sheetNamePattern || '-'}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Data Structure">
              <Tag color="blue">{fingerprint.structure?.dataStructure || '-'}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Header Row">
              {fingerprint.headerSignature?.row || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Hours Columns">
              {fingerprint.hoursColumns?.start && fingerprint.hoursColumns?.end
                ? `${fingerprint.hoursColumns.start} - ${fingerprint.hoursColumns.end} (${fingerprint.hoursColumns.count} cols)`
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="JMBG Location">
              {fingerprint.identifierLocation?.present
                ? fingerprint.identifierLocation.cellAddress
                : 'Not found'}
            </Descriptions.Item>
            <Descriptions.Item label="Languages" span={2}>
              {fingerprint.languageHints?.map((lang) => (
                <Tag key={lang} color="purple">{lang}</Tag>
              )) || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Skip Sheets" span={2}>
              {fingerprint.detectedSkipSheets?.join(', ') || 'None'}
            </Descriptions.Item>
            <Descriptions.Item label="Hash">
              <Text code copyable style={{ fontSize: 11 }}>
                {fingerprint.headerSignature?.patternHash || '-'}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Confidence">
              <Progress
                percent={fingerprint.confidence || 0}
                size="small"
                status={fingerprint.confidence >= 80 ? 'success' : 'normal'}
              />
            </Descriptions.Item>
          </Descriptions>
        </Panel>
      </Collapse>
    );
  };

  const renderSuggestions = () => {
    if (!suggestionResult) return null;

    const { suggestions, recommendedAction, fileInfo, fingerprint } = suggestionResult;

    return (
      <div style={{ marginTop: 16 }}>
        {/* File Info */}
        <Alert
          type="info"
          showIcon
          icon={fileInfo?.isEml ? <MailOutlined /> : <FileExcelOutlined />}
          message={
            <Space>
              <Text strong>{fileInfo?.filename}</Text>
              {fileInfo?.isEml && (
                <Text type="secondary">
                  ({fileInfo.excelFiles?.length || 0} Excel attachment(s))
                </Text>
              )}
            </Space>
          }
          description={
            fileInfo?.emlMetadata && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Subject: {fileInfo.emlMetadata.subject}
              </Text>
            )
          }
          style={{ marginBottom: 16 }}
        />

        {/* Fingerprint */}
        {renderFingerprint(fingerprint)}

        {/* Suggestions */}
        {suggestions?.length > 0 ? (
          <>
            <Title level={5} style={{ marginTop: 16, marginBottom: 8 }}>
              <ThunderboltOutlined /> Suggested Configurations
            </Title>
            <List
              bordered
              dataSource={suggestions}
              renderItem={(item, index) => (
                <List.Item
                  actions={[
                    <Button
                      key="select"
                      type={index === 0 && item.score >= 90 ? 'primary' : 'default'}
                      onClick={() => handleSelectConfig(item)}
                    >
                      Use This
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <div style={{ textAlign: 'center', minWidth: 60 }}>
                        <Progress
                          type="circle"
                          percent={item.score}
                          width={50}
                          strokeColor={getScoreColor(item.score)}
                          format={(percent) => `${percent}%`}
                        />
                      </div>
                    }
                    title={
                      <Space>
                        <Text strong>{item.configName}</Text>
                        {getRecommendationTag(item.recommendation)}
                      </Space>
                    }
                    description={
                      <Space direction="vertical" size={0}>
                        {item.description && (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {item.description}
                          </Text>
                        )}
                        <Space size={4} style={{ marginTop: 4 }}>
                          <Tooltip title="Header structure match">
                            <Tag color={item.matchDetails?.headerMatch ? 'green' : 'default'}>
                              Header {item.matchDetails?.headerMatch ? '✓' : '✗'}
                            </Tag>
                          </Tooltip>
                          <Tooltip title="Data structure match">
                            <Tag color={item.matchDetails?.structureMatch ? 'green' : 'default'}>
                              Structure {item.matchDetails?.structureMatch ? '✓' : '✗'}
                            </Tag>
                          </Tooltip>
                          <Tooltip title="Column positions match">
                            <Tag color={item.matchDetails?.columnsMatch ? 'green' : 'default'}>
                              Columns {item.matchDetails?.columnsMatch ? '✓' : '✗'}
                            </Tag>
                          </Tooltip>
                          <Tooltip title="JMBG location match">
                            <Tag color={item.matchDetails?.identifierMatch ? 'green' : 'default'}>
                              JMBG {item.matchDetails?.identifierMatch ? '✓' : '✗'}
                            </Tag>
                          </Tooltip>
                        </Space>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </>
        ) : (
          <Empty
            description="No matching configurations found"
            style={{ marginTop: 24 }}
          >
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateNew}>
              Create New Configuration
            </Button>
          </Empty>
        )}

        {/* Action based on recommendation */}
        {suggestions?.length > 0 && recommendedAction === 'create_new' && (
          <Alert
            type="warning"
            showIcon
            message="Low match scores"
            description="The existing configurations don't match well with this file structure. Consider creating a new configuration."
            style={{ marginTop: 16 }}
            action={
              <Button size="small" onClick={handleCreateNew}>
                Create New
              </Button>
            }
          />
        )}
      </div>
    );
  };

  return (
    <Card
      title={
        <Space>
          <SearchOutlined />
          <span>Configuration Suggestion</span>
        </Space>
      }
      extra={
        file && (
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            onClick={handleAnalyze}
            loading={suggestMutation.isPending}
          >
            Analyze & Suggest
          </Button>
        )
      }
    >
      <Paragraph type="secondary">
        Upload an EML or Excel file to automatically find matching parser configurations
        based on structural analysis.
      </Paragraph>

      <Dragger
        name="file"
        multiple={false}
        accept=".eml,.xlsx,.xls"
        beforeUpload={() => false}
        onChange={handleFileUpload}
        fileList={file ? [file] : []}
        disabled={suggestMutation.isPending}
      >
        <p className="ant-upload-drag-icon">
          <UploadOutlined />
        </p>
        <p className="ant-upload-text">Click or drag file to analyze</p>
        <p className="ant-upload-hint">
          Supports EML files with Excel attachments or direct Excel files (.xlsx, .xls)
        </p>
      </Dragger>

      {suggestMutation.isPending && (
        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <Spin tip="Analyzing file structure..." />
        </div>
      )}

      {renderSuggestions()}
    </Card>
  );
}
